"use client";

import { NavLink } from "./nav-link";
import { ThemeSwitchButton, ThemeSwitchIconButton } from "./theme-switch-button";
import { HStack, Box, Stack } from "@/styled-system/jsx";
import { center } from "@/styled-system/patterns";
import { css } from "@/styled-system/css";
import Link from "next/link";
import SearchBar from "./search-bar";
import { Portal, rem, ActionIcon, Group, Modal } from "@mantine/core";
import { useAtom } from "jotai";
import { hiddenAtom } from "@/store/atoms";
import { usePathname } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import SignOutButton from "../Button/SignOutButton";
import SignInButton from "../Button/SignInButton";
import Logo from "../Icons/Logo";
import LogoLetter from "../Icons/LogoLetter";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { em } from "@mantine/core";
import { useMounted } from "./use-mounted";
import classes from "../Modal/modal.module.css";
import FacebookIcon from "../Icons/FacebookIcon";
import InstaIcon from "../Icons/InstaIcon";
import MenuIcon from "../Icons/MenuIcon";
import Close from "../Icons/Close";

export function NavbarInteractive({ session }) {
  const mounted = useMounted();
  const isMobile = useMediaQuery(`(max-width: ${em(750)})`);
  const pathname = usePathname();
  const [page, setPage] = useState(undefined);
  const [hidden] = useAtom(hiddenAtom);
  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    if (pathname === "/disc-search") {
      setPage(true);
    } else {
      setPage(false);
    }
  }, [pathname]);

  // Don't render anything until client-side hydration is complete
  if (!mounted) {
    return null;
  }

  // Hide server navbar once client navbar is mounted
  const hideServerNavbar = (
    <style dangerouslySetInnerHTML={{
      __html: `.navbar-server { display: none !important; }`
    }} />
  );

  if (isMobile) {
    return (
      <>
        {hideServerNavbar}
        <Portal>
          <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: rem(60),
            zIndex: 10,
            transition: "transform 400ms ease",
          }}
        >
          <HStack
            height="16"
            py="5"
            px="4"
            position="relative"
            justify="space-between"
            backdropFilter="auto"
            backdropBlur="sm"
            bg={{ base: "lightAlpha", _dark: "darkAlpha" }}
          >
            <Box w="100px">
              <Link
                href="/"
                prefetch={false}
                className={center({ flexShrink: "0" })}
                aria-label="Home Page"
              >
                <LogoLetter />
              </Link>
            </Box>
            {page && <SearchBar />}
            <HStack gap="4">
              {!session ? (
                <SignInButton size={"xs"} />
              ) : (
                <SignOutButton size={"xs"} />
              )}
              <button
                onClick={open}
                type="button"
                aria-label={"Menu Button"}
                className={css({
                  px: "1",
                  py: "1",
                  color: "var(--colors-bg-darktolight)",
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  _hover: {
                    color: "brand",
                  },
                })}
              >
                <MenuIcon style={{ fontSize: "32px" }} />
              </button>
              <Modal
                className={classes.modal}
                classNames={{
                  content: classes.content,
                  header: classes.header,
                  body: classes.body,
                }}
                opened={opened}
                onClose={close}
                radius="lg"
                withCloseButton={false}
              >
                <button
                  onClick={close}
                  type="button"
                  aria-label={"Menu Button"}
                  className={css({
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                    px: "1",
                    py: "1",
                    textDecoration: "none",
                    border: "none",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    _hover: {
                      color: "brand",
                    },
                  })}
                >
                  <Close
                    className={css({
                      width: "1em",
                      height: "auto",
                      textDecoration: "none",
                      color: "var(--colors-bg-darktolight)",
                    })}
                    aria-label={"Close Button"}
                  />
                </button>
                <Stack align="center" px={"75px"} py={"25px"}>
                  <ThemeSwitchIconButton />
                  <NavLink href="/disc-search" close={close}>
                    Search
                  </NavLink>
                  <NavLink href="/advanced-disc-finder" close={close}>
                    Advanced Search
                  </NavLink>
                  <NavLink href="/disc-multi-search" close={close}>
                    MultiSearch
                  </NavLink>
                  <NavLink href="/disc-brands" close={close}>
                    Brands
                  </NavLink>
                  {session && (
                    <NavLink href="/account" close={close}>
                      Account
                    </NavLink>
                  )}
                  {!session ? <SignInButton /> : <SignOutButton />}
                  <Group gap="xs" justify="flex-end" wrap="nowrap" pt={"10px"}>
                    <ActionIcon
                      component="a"
                      size="lg"
                      variant="default"
                      radius="xl"
                      href="https://www.facebook.com/people/Stacks-on-Stacks/100089439401540/"
                      aria-label="Facebook Stacks on Stacks"
                    >
                      <FacebookIcon />
                    </ActionIcon>
                    <ActionIcon
                      component="a"
                      size="lg"
                      variant="default"
                      radius="xl"
                      href="https://www.instagram.com/stacksonstacksdg/"
                      aria-label="Instagram Stacks on Stacks"
                    >
                      <InstaIcon />
                    </ActionIcon>
                  </Group>
                </Stack>
              </Modal>
            </HStack>
          </HStack>
        </Box>
      </Portal>
      </>
    );
  }

  // Desktop view
  return (
    <>
      {hideServerNavbar}
      <Portal>
      <Box
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: rem(60),
          zIndex: 10,
          transform: `translate3d(0, ${hidden ? 0 : rem(-110)}, 0)`,
          transition: "transform 400ms ease",
        }}
      >
        <HStack
          height="18"
          py="8"
          pr="10"
          pl="10"
          position="relative"
          justify="space-between"
          backdropFilter="auto"
          backdropBlur="sm"
          bg={{ base: "lightAlpha", _dark: "darkAlpha" }}
        >
          <Link
            href="/"
            prefetch={false}
            className={center({ flexShrink: "0" })}
            aria-label="Home Page"
          >
            <Logo />
          </Link>
          {page && <SearchBar />}
          <HStack gap="4">
            <NavLink href="/disc-search" close={close}>
              Search
            </NavLink>
            <NavLink href="/advanced-disc-finder" close={close}>
              Advanced
            </NavLink>
            <NavLink href="/disc-multi-search" close={close}>
              MultiSearch
            </NavLink>
            <NavLink href="/disc-brands" close={close}>
              Brands
            </NavLink>
            {session && (
              <NavLink href="/account" close={close}>
                Account
              </NavLink>
            )}
            {!session ? <SignInButton /> : <SignOutButton />}
            <ThemeSwitchButton />
          </HStack>
        </HStack>
      </Box>
      <Suspense>
        <Box
          style={{
            zIndex: 1000000,
            position: "absolute",
            top: 30,
            paddingLeft: "70px",
            scale: "150%",
            transform: `translate3d(0, ${!hidden ? 0 : rem(-50)}, 0)`,
            transition: "transform 400ms ease",
          }}
        >
          <Link
            href="/"
            className={center({ flexShrink: "0" })}
            aria-label="Home Page"
          >
            <LogoLetter />
          </Link>
        </Box>
      </Suspense>
    </Portal>
    </>
  );
}
